import { IInputServicesBase } from "global/interfaces/controller.interface";
import { fromEvent, Subscription } from "rxjs";
import { map } from "rxjs/operators";

export enum InputClasses {
    errorClass = 'uk-form-danger',
    hiddenClass = 'uk-hidden'
}
export class InputServices implements IInputServicesBase {

    private observedInputs: Record<string, Subscription> = {}

    addFocusToInput({ element, inputName }: { element: HTMLElement, inputName: string }): void {
        const input = element?.querySelector(`input[name='${inputName}']`)
        if (input) (input as HTMLInputElement).focus()
    }

    observeElementEvents(element: HTMLInputElement | HTMLSelectElement | HTMLButtonElement, inputEvent: 'input' | 'change' | 'click', onChange: (value: string, event: InputEvent) => any): void {
        const inputToObserve$ = fromEvent(element, inputEvent);

        this.observedInputs[element.name] = inputToObserve$
            .pipe(map(this.mapInputEvent)).subscribe(({ value, event }) => this.subscribeInputWithErrorRemovalOnInputChange({
                value, event, onChange
            }));
    }

    /**
     * This method is in charge of adding error classes to the correct element within an input group on a defined element
     * @param {HTMLElement | null} element - The element to update
     * @param {Record<string, string>} errors - The errors to manage
     */
    addErrorsToFieldset({ element, errors, onErrorsUpdated }: { element: HTMLElement | null, errors: Record<string, string>, onErrorsUpdated?: () => void }): void {
        if (element) {
            Object.keys(errors).forEach((inputName) => {
                const input = element?.querySelector(`input[name='${inputName}']`) as HTMLElement;

                if (input) {
					const errorMessage = errors[inputName] || '';
					this.addOrRemoveFormValidationError('SHOW_VALIDATION_ERROR', input, errorMessage);
                }
            })
            if (onErrorsUpdated) onErrorsUpdated();
            if (errors) {
                const inputNameToFocus = Object.keys(errors)[0]
                const firstErrorElement = element?.querySelector(`input[name='${inputNameToFocus}']`)
                if (firstErrorElement) firstErrorElement.scrollIntoView()
                if (inputNameToFocus) this.addFocusToInput({ element, inputName: inputNameToFocus })
            }
        } else {
            throw new Error(`ADD ERRORS TO FIELDSET: Looks like the element you want to update doesn't exist`)
        }
    }

	addOrRemoveFormValidationError(showOrHide: 'SHOW_VALIDATION_ERROR' | 'HIDE_VALIDATION_ERROR', input: HTMLElement, errorMessage: string = ''): void {
		if (!input) return;

		const label = input.previousElementSibling;
		const errorMessageContainer = input.nextElementSibling

		if (showOrHide === 'SHOW_VALIDATION_ERROR') {

			// add error class to label
			if (label && label.classList) label.classList.add(InputClasses.errorClass);
			// add error class to input
			input.classList.add(InputClasses.errorClass)
			// if there's an error message, inject it, show the div and add an error class
			if (errorMessageContainer) {
				errorMessageContainer.classList.add(InputClasses.errorClass)
				errorMessageContainer.classList.remove(InputClasses.hiddenClass)
				errorMessageContainer.innerHTML = errorMessage
			}

		} else if (showOrHide === 'HIDE_VALIDATION_ERROR') {

			// remove error class from label
			if (label && label.classList) label.classList.remove(InputClasses.errorClass);
			// remove error class from input
			input.classList.remove(InputClasses.errorClass)
			// remove error message and class
			if (errorMessageContainer) {
				errorMessageContainer.classList.remove(InputClasses.errorClass)
				errorMessageContainer.classList.add(InputClasses.hiddenClass)
				errorMessageContainer.innerHTML = ''
			}

		}
	}

    subscribeInputWithErrorRemovalOnInputChange = ({ value, event, onChange }: {
        value: string,
        event: InputEvent,
        onChange?: (value: string, event: InputEvent) => void
    }): void => {

        const castEvent = (event.target as HTMLElement)

        const label = castEvent && castEvent.previousElementSibling
        if (label && label.classList.contains(InputClasses.errorClass)) label.classList.remove(InputClasses.errorClass)

        const input = (event.target as HTMLElement)
        if (input && input.classList.contains(InputClasses.errorClass)) input.classList.remove(InputClasses.errorClass)

        const errorMessage = castEvent && castEvent.nextElementSibling
        if (errorMessage) {
            errorMessage.classList.remove(InputClasses.errorClass)
            errorMessage.classList.add('uk-hidden')
        }

        if (onChange) onChange(value, event)
    }

    mapInputEvent = (event: InputEvent): { value: string, event: InputEvent } => {
        return { value: (event.target as HTMLInputElement | HTMLSelectElement).value, event }
    }

}
